/**
 * O botão da MPC — o mesmo do template da home.
 *
 * Pílula, texto na caixa em que foi escrito e um gesto só de hover em todo o
 * produto: uma camada colada no rodapé do botão cresce até tomá-lo inteiro. As
 * variantes são as três do template (acento, contorno e branco) mais uma de
 * apoio para dentro de cartão.
 *
 * A camada mora no CSS (`.mpc-btn-bg`, em `theme.css`) porque ela é a mesma em
 * todas as variantes; o que muda é só qual cor sobe. O rótulo vai dentro de um
 * `<span class="relative">` para ficar por cima dela — sem isso o texto some
 * atrás do fundo no meio da transição.
 *
 * Existe um `Botao` e um `BotaoLink` em vez de uma prop `as`: a diferença entre
 * os dois é o tipo das props (um tem `onClick` e `disabled`, o outro tem `to`),
 * e uma prop polimórfica compraria genéricos ilegíveis para economizar oito
 * linhas.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cn } from '@/lib/cn';

export type VarianteBotao = 'primario' | 'contorno' | 'claro' | 'superficie';
export type TamanhoBotao = 'sm' | 'md' | 'lg';

const VARIANTE: Record<VarianteBotao, string> = {
  primario: 'mpc-btn-primary',
  contorno: 'mpc-btn-ghost',
  claro: 'mpc-btn-light',
  superficie: 'mpc-btn-surface',
};

/*
 * `lg` é o botão da hero, medida por medida (1.25rem × 2.5rem, texto de
 * 1.125rem). `md` e `sm` descem a mesma proporção para caber em cartão e em
 * linha de tabela, onde o botão de cartaz ocuparia o espaço do conteúdo.
 */
const TAMANHO: Record<TamanhoBotao, string> = {
  sm: 'px-4 py-2.5 text-sm',
  md: 'px-7 py-3.5 text-base',
  lg: 'px-10 py-5 text-lg',
};

interface Comum {
  variante?: VarianteBotao;
  tamanho?: TamanhoBotao;
  /** Ocupa a linha inteira — usado dentro de card estreito e no mobile. */
  cheio?: boolean;
  className?: string;
  children: ReactNode;
}

function classes({ variante = 'primario', tamanho = 'md', cheio, className }: Comum) {
  return cn('mpc-btn', VARIANTE[variante], TAMANHO[tamanho], cheio && 'w-full', className);
}

/**
 * O miolo: a camada que sobe e o rótulo por cima dela. Sai numa função porque
 * os dois componentes precisam da mesma estrutura, e um deles esquecer o
 * `aria-hidden` da camada seria um leitor de tela anunciando um span vazio.
 */
function Miolo({ children }: { children: ReactNode }) {
  return (
    <>
      <span aria-hidden className="mpc-btn-bg" />
      <span className="relative flex items-center gap-2">{children}</span>
    </>
  );
}

export function Botao({
  variante,
  tamanho,
  cheio,
  className,
  children,
  ...props
}: Comum & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        classes({ variante, tamanho, cheio, className, children }),
        'disabled:pointer-events-none disabled:opacity-45',
      )}
    >
      <Miolo>{children}</Miolo>
    </button>
  );
}

export function BotaoLink({
  variante,
  tamanho,
  cheio,
  className,
  children,
  ...props
}: Comum & LinkProps) {
  return (
    <Link {...props} className={classes({ variante, tamanho, cheio, className, children })}>
      <Miolo>{children}</Miolo>
    </Link>
  );
}

/** A seta que acompanha o CTA principal e desliza no hover. */
export function SetaBotao() {
  return (
    <svg
      className="size-3.5 transition-transform duration-[--duration-quick] ease-[--ease-signature] group-hover:translate-x-1"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <path
        d="M2 8h11M9 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
